// components/ClientCarousel.tsx
"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import Image from "next/image";

import { getFeaturedClients } from "@/lib/data/clients";
import type { Client } from "@/lib/data";
import { Typography } from "@/components/ui";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";

interface ClientCarouselProps {
  title?: string;
  clients?: Client[];
  iconWidth?: number;
}

export default function ClientCarousel({ title, clients, iconWidth = 160 }: ClientCarouselProps) {
  const featuredClients = clients || getFeaturedClients();

  return (
    <section className="py-12">
      <div className="container mx-auto px-4">
        {title && (
          <div className="text-center mb-8">
            <Typography variant="h2" as="h2">
              {title}
            </Typography>
          </div>
        )}
        <div className="swiper-container relative">
          <Swiper
            modules={[Autoplay, Navigation, Pagination]}
            spaceBetween={30}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 2 },
              768: { slidesPerView: featuredClients.length > 3 ? 3 : featuredClients.length },
              1024: { slidesPerView: featuredClients.length > 4 ? 4 : featuredClients.length },
              1280: { slidesPerView: featuredClients.length > 6 ? 6 : featuredClients.length },
            }}
            autoplay={{
              delay: 2500,
              disableOnInteraction: true,
              waitForTransition: true,
            }}
            loop={false}
            navigation={false}
            pagination={false}
            speed={800}
            centeredSlides={false}
            className="client-swiper"
          >
            {featuredClients.map(client => (
              <SwiperSlide key={client.id}>
                <div className="flex items-center justify-center h-20">
                  <Image
                    src={client.logo}
                    alt={`${client.name} logo`}
                    title={`${client.name} logo`}
                    width={iconWidth}
                    height={80}
                    loading="lazy"
                    decoding="async"
                    className="max-w-full h-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                    style={{ width: iconWidth + "px", height: "auto" }}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
}
